import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiError, Paginated } from "@shared/http";
import type { CapacityReport as CapacityBase } from "@shared/capacity";
export type { FitAxis } from "@shared/fit";
import type { FitAxis } from "@shared/fit";

/**
 * API 客戶端
 * ---------------------------------------------------------------
 * 所有請求都打同源的 /api/*，由前端 Worker 轉給 gateway。
 * 好處：不用處理 CORS、cookie 自動帶上、少一次 DNS 與 TLS 交握。
 *
 * ── 市場一定要跟著請求走 ──────────────────────────────────
 *
 * 這裡原本寫著「市場不需要前端傳，gateway 會依登入身分或 IP 判定」。
 * 那句話在未登入時是錯的，而且錯得很難發現：
 *
 *   人在台灣的訪客打開 /us/en/talent
 *   → 網址說美國、頁首徽章說 United States
 *   → API 完全看不到網址，只看得到 IP，於是回傳台灣的接案者
 *
 * 市場隔離是這個平台的核心承諾，而使用者看到的第一個證據
 * 就是它沒有被遵守。
 *
 * 現在每一個請求都帶 X-Market，值來自網址 —— 網址是瀏覽時的
 * 真相來源（SEO 索引的是它、頁首顯示的也是它）。
 *
 * 登入之後 gateway 仍然一律以帳號綁定的市場為準，
 * 這個標頭不會讓任何人跨市場交易。
 */

/**
 * 目前網址上的市場代碼。由 SiteProvider 在每次市場變動時寫入。
 *
 * 放在模組層級而不是 context —— request() 不是元件也不是 hook，
 * 它會在事件處理與 catch 區塊裡被呼叫，那些地方拿不到 context。
 */
let activeMarket: string | null = null;

export function setActiveMarket(code: string | null): void {
  activeMarket = code;
}

export class ApiRequestError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly fields?: Record<string, string[]>,
    /**
     * 伺服器附帶的資料。
     *
     * 給「不是失敗、而是流程還沒完成」的情況用 —— 二步驗證回
     * two_factor_required 時，challenge token 與遮罩過的信箱就在這裡。
     * 以前這一欄被丟掉，於是那個流程在前端無法繼續：
     * 錯誤訊息收得到，接下去要用的東西收不到。
     */
    readonly details?: Record<string, unknown>,
    /**
     * 這句話在字典裡的 key。
     *
     * 伺服器端的訊息是寫給開發者看的英文（或以前是中文），
     * 使用者要看的是這個 key 翻出來的那一句。有 key 就翻它，
     * 沒有才退回 message —— 還沒改完的呼叫點照常運作。
     */
    readonly key?: string,
    /**
     * key 的插值參數。
     *
     * 「作品最多 12 件」裡的 12 只有伺服器知道 —— 前端再抄一份
     * 常數，就會有一天兩邊對不上，而使用者看到的是錯的那一個。
     */
    readonly params?: Record<string, string | number>,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...(activeMarket ? { "x-market": activeMarket } : {}),
      ...init?.headers,
    },
    // cookie 是 HttpOnly 的 session，一定要帶
    credentials: "same-origin",
  });

  const payload = (await res.json().catch(() => null)) as
    | { data: T }
    | ApiError
    | null;

  if (!res.ok || !payload || "error" in payload) {
    const error = (payload as ApiError | null)?.error;
    throw new ApiRequestError(
      error?.code ?? "internal_error",
      error?.message ?? `Request failed (${res.status})`,
      res.status,
      error?.fields,
      error?.details,
      error?.key,
      error?.params,
    );
  }

  return payload.data;
}

/** query 物件 → query string，會自動略過 undefined 與空字串 */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const result = search.toString();
  return result ? `?${result}` : "";
}

/** 檢舉的分類。伺服器端的 REPORT_REASONS 要和這裡一致。 */
export type ReportReason =
  | "scam"
  | "offPlatform"
  | "impersonation"
  | "harassment"
  | "spam"
  | "fakeWork"
  | "illegal"
  | "other";

export type ReportTarget = "user" | "job" | "message" | "proposal";

export interface MyReport {
  id: string;
  targetKind: ReportTarget;
  targetId: string;
  reason: ReportReason;
  status: string;
  createdAt: string;
}

export interface BlockedPerson {
  userId: string;
  createdAt: string;
  displayName: string | null;
  handle: string | null;
  avatarUrl: string | null;
}

export const api = {
  bootstrap: () => request<BootstrapData>("/bootstrap"),
  me: () => request<Actor>("/me"),

  // ── 檢舉與封鎖 ────────────────────────────────────────────────

  report: (input: {
    targetKind: ReportTarget;
    targetId: string;
    targetUserId?: string;
    reason: ReportReason;
    detail?: string;
  }) =>
    request<{ id: string; updated: boolean }>("/me/reports", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  myReports: () => request<{ reports: MyReport[] }>("/me/reports"),

  /**
   * 案件的私人聯絡資料。
   *
   * ── 這支端點在後端存在很久了，但前端從來沒有呼叫過 ──────
   *
   * 發案方在刊登時填了電話與地址，資料存進去了，然後**沒有任何
   * 畫面顯示它** —— 得標的接案者拿不到，發案方自己也看不到。
   *
   * 揭露規則在伺服器端（services/jobs 的 /jobs/:id/contact）：
   * 發案方本人隨時看得到；接案者要等到真的成立合約。
   * 那個規則是這個平台會員費的基礎 —— 未登入或未得標的人
   * 看得到聯絡方式的話，就沒有人需要付費了。
   *
   * 403 是**正常的回應**（還沒得標），呼叫端要把它當成
   * 「還沒解鎖」而不是錯誤。
   */
  jobContact: (jobId: string) =>
    request<{ phone: string | null; address: string | null; visibility?: string }>(
      `/jobs/${jobId}/contact`,
    ),

  // ── 隱私權 ────────────────────────────────────────────────────

  /**
   * 帳號刪除的目前狀態。
   *
   * 排定了才有 scheduledAt —— 前端用它決定要顯示「刪除帳號」
   * 還是「你的帳號將於…清除」。
   */
  deletionStatus: () =>
    request<{ requestedAt: string | null; scheduledAt: string | null; graceDays: number }>(
      "/me/delete",
    ),

  requestDeletion: (input: { confirmEmail: string; reason?: string }) =>
    request<{ scheduledAt: string; graceDays: number }>("/me/delete", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  cancelDeletion: () => request<{ cancelled: boolean }>("/me/delete", { method: "DELETE" }),

  blocks: () => request<{ blocks: BlockedPerson[] }>("/me/blocks"),

  block: (userId: string) =>
    request<{ blocked: boolean }>("/me/blocks", {
      method: "POST",
      body: JSON.stringify({ userId }),
    }),

  unblock: (userId: string) =>
    request<{ blocked: boolean }>(`/me/blocks/${userId}`, { method: "DELETE" }),


  login: (input: { email: string; password: string }) =>
    request<{ user: Actor }>("/auth/login", { method: "POST", body: JSON.stringify(input) }),

  signup: (input: Record<string, unknown>) =>
    request<{ user: Actor }>("/auth/signup", { method: "POST", body: JSON.stringify(input) }),

  logout: () => request<{ ok: boolean }>("/auth/logout", { method: "POST" }),

  /**
   * 取得 Google 授權網址。拿到之後由呼叫端用 location.assign 離開本站。
   *
   * 不在這裡直接跳轉：這一層的職責是「打 API」，跳轉是畫面的決定。
   * 混在一起的話，錯誤處理（顯示訊息還是跳走）就沒有地方可放。
   *
   * 帶 role 表示註冊意圖，伺服器會一併檢查條款版本；
   * 不帶就是登入既有帳號。
   */
  googleStart: (input: {
    role?: "client" | "freelancer" | "both";
    countryCode?: string;
    redirectTo?: string;
    termsVersion?: string;
    privacyVersion?: string;
    cookiesVersion?: string;
  }) =>
    request<{ url: string }>("/auth/google/start", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  forgotPassword: (email: string) =>
    request<{ sent: boolean }>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token: string, password: string) =>
    request<{ user: Actor }>("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }),

  categories: () => request<Category[]>("/categories"),
  skills: (category?: string) => request<Skill[]>(`/skills${qs({ category })}`),

  jobs: (params: Record<string, string | number | undefined>) =>
    request<Paginated<JobSummary>>(`/jobs${qs(params)}`),

  job: (slug: string) => request<JobDetail>(`/jobs/${slug}`),

  createJob: (input: Record<string, unknown>) =>
    request<{ id: string; slug: string }>("/jobs", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  talent: (params: Record<string, string | number | undefined>) =>
    request<Paginated<TalentSummary>>(`/talent${qs(params)}`),

  talentProfile: (handle: string) => request<TalentDetail>(`/talent/${handle}`),

  activity: (limit = 12) => request<ActivityItem[]>(`/activity${qs({ limit })}`),
  stats: () => request<MarketStats>("/stats"),

  jobSlots: (jobId: string) => request<ProposalSlots>(`/jobs/${jobId}/slots`),

  createProposal: (input: Record<string, unknown>) =>
    request<CreateProposalResult>("/proposals", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  // ── 接案者的檔案與作品集 ───────────────────────────────────
  // 路徑上沒有 userId —— 改的是誰由 session 決定，所以不可能改到別人的。

  /** 我的成效數據。只回真的有在收集的東西。 */
  /**
   * 這個市場實際有人用的領域 —— 包含接案者自己填的。
   *
   * 和 categories() 不同：那一支是策展的分類（有圖示與代表色，
   * 用在分類頁的圖騰）。這一支回的是「有誰在用」的事實。
   */
  talentCategories: () => request<{ slug: string; count: number }[]>("/talent-categories"),

  analytics: () => request<Analytics>("/me/analytics"),

  myFreelancerProfile: () => request<FreelancerProfileForm>("/me/freelancer-profile"),

  saveFreelancerProfile: (input: FreelancerProfileForm) =>
    request<{ ok: true; skills: string[] }>("/me/freelancer-profile", {
      method: "PUT",
      body: JSON.stringify(input),
    }),

  myPortfolio: () => request<PortfolioItem[]>("/me/portfolio"),

  /**
   * 完工的案子，整理成可以直接放進作品集的樣子。
   *
   * 作品集是接案者最知道自己該做、但最不會去做的一件事 ——
   * 因為每一筆都要重新回想「那個案子是什麼、做了多久、客戶說了什麼」。
   * 那三件事平台都有。
   */
  completedProjects: () => request<{ projects: CompletedProject[] }>("/office/completed"),

  // ── 支出與 AI ──────────────────────────────────────────────
  // 這兩項是全站唯二成本會隨使用量成長的東西，所以都受
  // packages/shared/src/ai-quota.ts 的額度控制。

  expenses: (year: number) =>
    request<{ year: number; expenses: Expense[]; summary: ExpenseSummary[] }>(
      `/office/expenses?year=${year}`,
    ),

  addExpense: (input: ExpenseInput) =>
    request<{ id: string }>("/office/expenses", { method: "POST", body: JSON.stringify(input) }),

  deleteExpense: (id: string) =>
    request<{ ok: true }>(`/office/expenses/${id}`, { method: "DELETE" }),

  /** 拍照辨識。讀不出來也會建一筆讓他自己填 —— 照片已經存起來了 */
  scanReceipt: (blob: Blob) =>
    request<{ id: string; spentAt: string; amount: number; vendor: string; needsReview: boolean }>(
      "/office/expenses/scan",
      { method: "POST", headers: { "content-type": blob.type || "image/webp" }, body: blob },
    ),

  /** AI 助手。上下文由伺服器組 —— 讓使用者貼的話就沒有理由不用 ChatGPT */
  assist: (input: { task: string; targetId?: string; hint: string }) =>
    request<{ text: string }>("/office/assist", { method: "POST", body: JSON.stringify(input) }),

  aiQuota: () => request<AiQuota>("/office/ai-quota"),

  // ── 離線代理人 ─────────────────────────────────────────────
  // 他離線時替他問完該問的。**不報價、不承諾工期、不答應接案** ——
  // 那三件事一旦被說出口，在對方眼中就是他答應的。

  agent: () => request<AgentSettings>("/agent"),

  saveAgent: (input: AgentSettings) =>
    request<{ enabled: boolean }>("/agent", { method: "PUT", body: JSON.stringify(input) }),

  /** 代理人替他問到了什麼。他回來看的第一頁 */
  agentCollected: () =>
    request<{ conversations: AgentCollected[] }>("/agent/collected"),

  createPortfolioItem: (input: PortfolioInput) =>
    request<{ id: string }>("/me/portfolio", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  updatePortfolioItem: (id: string, input: PortfolioInput) =>
    request<{ ok: true }>(`/me/portfolio/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),

  deletePortfolioItem: (id: string) =>
    request<{ ok: true }>(`/me/portfolio/${id}`, { method: "DELETE" }),

  reorderPortfolio: (ids: string[]) =>
    request<{ ok: true }>("/me/portfolio/reorder", {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),

  /** 圖片先上傳拿到網址，再連同其他欄位送出 */
  uploadPortfolioImage: (file: File) =>
    request<{ imageUrl: string }>("/me/portfolio/image", {
      method: "POST",
      headers: { "content-type": file.type },
      body: file,
    }),

  // ── 流動辦公室 ─────────────────────────────────────────────
  // 站外談成的案子也能拿進來管。平台依然不經手任何款項 ——
  // 這裡產生的是單據與紀錄，錢由雙方直接結算。

  officeSummary: () => request<OfficeSummary>("/office/summary"),

  // ── 財務 ───────────────────────────────────────────────────
  // 稅務試算在前端算（packages/shared/src/tax）——「再接這一案會不會
  // 跳級距」那個拉桿要即時反應，每動一格往返一次伺服器的話沒有人會用。
  // 所以這裡拿的是原始資料，不是算好的結果。

  finance: (year: number) => request<FinanceData>(`/office/finance?year=${year}`),

  // ── 客戶名冊 ───────────────────────────────────────────────
  // 名字原本散在每張請款單裡，是自由文字 —— 同一個客戶打三次、
  // 打錯一次就變兩個，而且沒辦法彙總。這裡的每一筆都帶著彙總，
  // 那才是他會回來看的東西。

  // ── 報價建議 ───────────────────────────────────────────────
  // 「這個分類的人通常報到預算的幾成」。只有平台看得到別人，
  // 所以這是個人工具做不到的一件事。

  // ── 產能 ───────────────────────────────────────────────────
  // 「再接這一個會不會爆」。接案者最常見的死法是同時答應太多案，
  // 而那很少是一次做錯決定，是連續三次「這個應該塞得下」。

  // ── 雙向媒合 ───────────────────────────────────────────────
  // 端點在 jobs 與 identity 裡寫好很久了，但 gateway 一直沒接上 ——
  // 前端叫不到，也就是說這個功能對每一個使用者都不存在。

  /**
   * 這個案子和我有多合。六個維度，判斷的規則在
   * packages/shared/src/fit.ts —— 那裡有測試。
   */
  jobFit: (jobId: string) => request<{ axes: FitAxis[] }>(`/jobs/${jobId}/fit`),

  matchingJobs: (limit = 4) =>
    request<{ matches: JobMatch[]; needsSkills: boolean }>(`/jobs/matching?limit=${limit}`),

  capacity: () => request<CapacityReport>("/office/capacity"),

  // ── 工時 ───────────────────────────────────────────────────
  // 「這個案子做到現在，我的時薪剩多少」。一個報價 NT$30,000 的
  // 案子做了六十小時，時薪是 500 —— 而他在做完之前不會知道。

  /**
   * 同儕對標。
   *
   * 「我這樣算多還是少」是每個接案者都在想、但沒有人問得出口的
   * 問題 —— 同行之間不談收入。只有平台看得到全部的人。
   */
  benchmark: (category: string) =>
    request<Benchmark>(`/office/benchmark?category=${encodeURIComponent(category)}`),

  /** 逐年的收入、案量、時薪與客戶回頭率 */
  growth: () =>
    request<{ years: GrowthYear[]; seasons: SeasonMonth[]; yearsOfData: number }>("/office/growth"),

  timeLog: () => request<TimeLog>("/office/time"),

  /** 開始計時。已經有一個在跑的話伺服器會先把它停掉 */
  startTimer: (contractId?: string) =>
    request<{ id: string; startedAt: string; stoppedPrevious: boolean }>(
      `/office/time/start${contractId ? `?contractId=${encodeURIComponent(contractId)}` : ""}`,
      { method: "POST" },
    ),

  stopTimer: () => request<{ id: string; minutes: number }>("/office/time/stop", { method: "POST" }),

  /** 手動補登 —— 人會忘記按開始，而只有計時器的工具會被放棄 */
  addTimeEntry: (input: TimeEntryInput) =>
    request<{ id: string }>("/office/time", { method: "POST", body: JSON.stringify(input) }),

  deleteTimeEntry: (id: string) =>
    request<{ ok: true }>(`/office/time/${id}`, { method: "DELETE" }),

  pricingGuidance: (params: { category: string; pricing: string; budgetMax: number }) =>
    request<PricingGuidance>(
      `/pricing?category=${encodeURIComponent(params.category)}&pricing=${params.pricing}&budgetMax=${params.budgetMax}`,
    ),

  clients: (includeArchived = false) =>
    request<{ clients: CrmClient[] }>(`/office/clients${includeArchived ? "?archived=1" : ""}`),

  createClient: (input: ClientInput) =>
    request<{ id: string }>("/office/clients", { method: "POST", body: JSON.stringify(input) }),

  updateClient: (id: string, input: ClientInput) =>
    request<{ ok: true }>(`/office/clients/${id}`, { method: "PUT", body: JSON.stringify(input) }),

  /*
    ── 把自己的客戶請進來 ──────────────────────────────────────

    這是這個平台獨有的東西：在抽成的站上，把既有客戶帶進去等於
    為一份已經談成的工作付 10%，所以沒有人會做。這裡不抽成，
    他沒有理由隱瞞，而平台換到的是一個發案方帳號。

    對他的好處要在第一句話就成立 —— 不是「幫我們拉人」，是
    「你的客戶自己看得到進度，你不用每次回報」。
  */
  /**
   * 關鍵字 → 發包文草稿。
   *
   * 案主的痛點不是找不到人，是不會寫需求。一個寫著「需要一個網站」
   * 的案子會收到二十份沒辦法比較的提案，而他讀完之後會說
   * 「這裡的人素質不好」—— 問題從頭到尾在那七個字。
   *
   * missing 是**刻意留白**的：模型不能替他編，所以它不知道的事會
   * 變成一份要他回答的清單，而不是說明裡的一句話。
   */
  draftJob: (keywords: string, categorySlug?: string) =>
    request<{
      draft: { title: string; description: string; skills: string[]; missing: string[] };
      usedThisMonth: number;
      cap: number;
    }>("/jobs/draft", {
      method: "POST",
      body: JSON.stringify({ keywords, categorySlug }),
    }),

  inviteClient: (id: string) =>
    request<{ id: string; sentTo: string; expiresInDays: number }>(
      `/office/clients/${id}/invite`,
      { method: "POST" },
    ),

  clientInviteStatus: (id: string) =>
    request<{ invite: ClientInviteStatus | null }>(`/office/clients/${id}/invite`),

  /** 註冊完成後把邀請連上。失敗不影響帳號 —— 他還是註冊成功了 */
  acceptClientInvite: (token: string) =>
    request<{ linked: boolean }>("/client-invites/accept", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),

  archiveClient: (id: string, undo = false) =>
    request<{ archived: boolean }>(`/office/clients/${id}/archive${undo ? "?undo=1" : ""}`, {
      method: "POST",
    }),

  /** 把 from 的單據搬到 id 底下，再封存 from。不是刪除 —— 併錯了資料還在 */
  mergeClients: (id: string, from: string) =>
    request<{ merged: true }>(`/office/clients/${id}/merge?from=${encodeURIComponent(from)}`, {
      method: "POST",
    }),

  saveTaxSettings: (year: number, input: TaxSettingsInput) =>
    request<{ year: number }>(`/office/finance/settings?year=${year}`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),

  addExternalIncome: (input: ExternalIncomeInput) =>
    request<{ id: string }>("/office/finance/external", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  deleteExternalIncome: (id: string) =>
    request<{ ok: true }>(`/office/finance/external/${id}`, { method: "DELETE" }),

  createProject: (input: ProjectInput) =>
    request<{ id: string }>("/office/projects", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  deleteProject: (id: string) =>
    request<{ ok: true }>(`/office/projects/${id}`, { method: "DELETE" }),

  // ── 文書 ───────────────────────────────────────────────────
  notes: (projectId: string) => request<ProjectNote[]>(`/office/projects/${projectId}/notes`),

  createNote: (projectId: string, input: NoteInput) =>
    request<{ id: string }>(`/office/projects/${projectId}/notes`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  updateNote: (id: string, input: NoteInput) =>
    request<{ ok: true }>(`/office/notes/${id}`, { method: "PUT", body: JSON.stringify(input) }),

  deleteNote: (id: string) => request<{ ok: true }>(`/office/notes/${id}`, { method: "DELETE" }),

  // ── 排程 ───────────────────────────────────────────────────
  tasks: () => request<ProjectTask[]>("/office/tasks"),

  createTask: (input: { contractId: string; title: string; dueAt: string }) =>
    request<{ id: string }>("/office/tasks", { method: "POST", body: JSON.stringify(input) }),

  /** 打勾與取消打勾同一支 —— 使用者的動作就是「按一下那個框」 */
  toggleTask: (id: string) =>
    request<{ doneAt: string | null }>(`/office/tasks/${id}/toggle`, { method: "POST" }),

  deleteTask: (id: string) => request<{ ok: true }>(`/office/tasks/${id}`, { method: "DELETE" }),

  // ── 進度分享 ───────────────────────────────────────────────
  setProgress: (projectId: string, input: { shared: boolean; note: string }) =>
    request<{ token: string; shared: boolean }>(`/office/projects/${projectId}/progress`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /** 客戶看到的進度頁。不需要登入。 */
  publicProgress: (token: string) => request<PublicProgress>(`/public/projects/${token}`),

    /** 預設只拿請款單。報價單傳 "quote" —— 兩者的動作完全不同，不混在一張清單裡 */
  invoices: (kind: "invoice" | "quote" = "invoice") =>
    request<Invoice[]>(`/office/invoices${kind === "quote" ? "?kind=quote" : ""}`),

  /** 報價單被接受 → 開一張請款單。原本那張標記為已接受，不會重複開 */
  acceptQuote: (id: string) =>
    request<{ id: string }>(`/office/invoices/${id}/accept`, { method: "POST" }),

  invoice: (id: string) => request<Invoice & { items: InvoiceLine[] }>(`/office/invoices/${id}`),

  /**
   * 合約的簽署文件。
   *
   * current 是還沒被取代的那一版；history 只回中繼資料
   * （版號、雜湊、時間），不回舊版全文 —— 一份長合約的每一版
   * 都塞進來，會讓詳情頁變成幾百 KB。
   */
  /**
   * 我的推薦碼與成績。
   *
   * monthsLeftThisYear 是年度上限剩下的額度 —— 它要顯示出來，
   * 因為暗著扣的話，第十三個推薦成功的人會發現自己沒拿到，
   * 而畫面上沒有任何地方解釋為什麼。
   */
  /**
   * 合作過的接案者。
   *
   * 只回完成過的平台合約 —— 一個做到一半就取消的合作不是
   * 「合作過」，而把它算進來的話，這份名單會混進發案方最不想
   * 再看到的那幾個人。
   */
  /**
   * 行情透明表。公開，不需要登入。
   *
   * 每一格都帶著來源與樣本數 —— 「發案方開的預算」「接案者報的
   * 價」「實際成交的金額」是三件完全不同的事，混為一談的話，
   * 讀的人會照著報價去談判，然後談不成。
   */
  /**
   * 我綁了哪些作品集帳號。
   *
   * OAuth 證明的是「這個人控制那個帳號」，不是「那些作品是他做的」。
   * 回傳的 stats 是公開統計的快照（帳號開了幾年、幾個 repo、
   * 幾個追蹤者）—— 那些數字偽造起來的成本，遠高於打一段自我介紹。
   */
  myPortfolioProofs: () =>
    request<
      {
        provider: string;
        handle: string;
        profileUrl: string;
        verifiedAt: string;
        stats: Record<string, unknown>;
      }[]
    >("/me/proofs"),

  /** 回傳的 url 要用整頁導向，不是 fetch —— OAuth 授權必須是使用者自己的瀏覽器導覽 */
  startPortfolioProof: (provider: string) =>
    request<{ url: string }>(`/me/proofs/${provider}/start`, { method: "POST" }),

  revokePortfolioProof: (provider: string) =>
    request<{ revoked: boolean }>(`/me/proofs/${provider}`, { method: "DELETE" }),

  rates: () =>
    request<{
      categories: {
        categorySlug: string;
        jobs: number;
        avgProposals: number;
        bid: { medianMinor: number | null; samples: number; source: string };
        settled: { medianMinor: number | null; samples: number; source: string };
      }[];
      minSamples: number;
      /** 現在拿不拿得出東西。全空的表比沒有這一頁更糟 */
      ready: boolean;
    }>("/rates"),

  workedWith: () =>
    request<
      {
        freelancerId: string;
        displayName?: string;
        jobs: number;
        lastAt: string;
        totalMinor: number;
        currency: string;
        /** 我給過他幾顆星。沒評價過是 null，不是 0 */
        myRating: number | null;
      }[]
    >("/office/worked-with"),

  jobInvites: (jobId: string) =>
    request<
      { id: string; freelancerId: string; status: string; createdAt: string }[]
    >(`/jobs/${jobId}/invites`),

  inviteToJob: (jobId: string, freelancerIds: string[], note: string) =>
    request<{ invited: number }>(`/jobs/${jobId}/invites`, {
      method: "POST",
      body: JSON.stringify({ freelancerIds, note }),
    }),

  myInvites: () =>
    request<
      {
        id: string;
        note: string;
        createdAt: string;
        expiresAt: string | null;
        job: { slug: string; title: string; budgetMinMinor: number; budgetMaxMinor: number };
      }[]
    >("/my/invites"),

  declineInvite: (id: string) =>
    request<{ status: string }>(`/my/invites/${id}/decline`, { method: "POST" }),

  referral: () =>
    request<{
      code: string | null;
      pending: number;
      qualified: number;
      monthsEarned: number;
      monthsLeftThisYear: number;
      yearlyCap: number;
      rewardMonths: number;
    }>("/me/referral"),

  claimReferral: (code: string) =>
    request<{ referrerId: string }>("/me/referral/claim", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),

  contractDocument: (id: string) =>
    request<{
      current: ContractDocument | null;
      history: {
        version: number;
        contentHash: string;
        createdAt: string;
        supersededAt: string | null;
        fullySignedAt: string | null;
      }[];
    }>(`/contracts/${id}/document`),

  saveContractDocument: (id: string, body: string) =>
    request<{ version: number; contentHash: string; newVersion: boolean }>(
      `/contracts/${id}/document`,
      { method: "PUT", body: JSON.stringify({ body }) },
    ),

  /**
   * 簽署。
   *
   * contentHash 是**使用者畫面上那一份**的雜湊，不是重新抓的 ——
   * 他讀完到按下去可能過了十分鐘，而另一方在這十分鐘裡可能改了
   * 條款。對不上時伺服器會拒絕，那正是這個參數存在的理由。
   */
  signContract: (id: string, signerName: string, contentHash: string) =>
    request<{ version: number; contentHash: string; signedAt: string; fullySigned: boolean }>(
      `/contracts/${id}/sign`,
      { method: "POST", body: JSON.stringify({ signerName, contentHash }) },
    ),

  createInvoice: (input: InvoiceInput, kind: "invoice" | "quote" = "invoice") =>
    request<{ id: string }>(`/office/invoices${kind === "quote" ? "?kind=quote" : ""}`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  updateInvoice: (id: string, input: InvoiceInput) =>
    request<{ ok: true }>(`/office/invoices/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),

  setInvoiceStatus: (id: string, status: DocStatus) =>
    request<{ ok: true }>(`/office/invoices/${id}/status`, {
      method: "POST",
      body: JSON.stringify({ status }),
    }),

  deleteInvoice: (id: string) =>
    request<{ ok: true }>(`/office/invoices/${id}`, { method: "DELETE" }),

  /**
   * 開關請款單的公開連結。
   *
   * rotate 會換一條新的：關掉分享擋得住「不該看的人繼續看」，
   * 但擋不住「我還是要分享，只是不想讓舊的那個人看到」——
   * 重新打開的是同一條連結。
   */
  setInvoiceShare: (id: string, shared: boolean, rotate = false) =>
    request<{ token: string; shared: boolean }>(`/office/invoices/${id}/share`, {
      method: "POST",
      body: JSON.stringify({ shared, rotate }),
    }),

  /** 公開的請款單。不需要登入 —— 客戶多半不是平台使用者。 */
  publicInvoice: (token: string) => request<PublicInvoice>(`/public/invoices/${token}`),

  // ── 會員費 ─────────────────────────────────────────────────
  // 這是平台唯一收錢的地方。案件款項由雙方直接結算，不經過這裡。

  /** 金額不由前端決定 —— 只送 cycle，價目表在後端。 */
  membershipCheckout: (cycle: "monthly" | "yearly") =>
    request<CheckoutSession>("/membership/checkout", {
      method: "POST",
      body: JSON.stringify({ cycle }),
    }),

  membershipOrder: (orderNo: string) => request<MembershipOrder>(`/membership/orders/${orderNo}`),

  membershipOrders: () => request<MembershipOrder[]>("/membership/orders"),

  // ── 訊息 ───────────────────────────────────────────────────

  conversations: () => request<Paginated<Conversation>>("/conversations?pageSize=50"),

  unreadCount: () => request<{ unread: number }>("/conversations/unread"),

  conversation: (id: string) =>
    request<{ conversation: Conversation; messages: Message[] }>(`/conversations/${id}`),

  sendMessage: (id: string, body: string) =>
    request<{ id: string; createdAt: string }>(`/conversations/${id}/messages`, {
      method: "POST",
      body: JSON.stringify({ body }),
    }),

  /**
   * 發起一段諮詢。已經有對話串的話回既有的那一串。
   */
  startInquiry: (input: {
    counterpartId: string;
    as: "client" | "freelancer";
    message: string;
    counterpartName?: string;
  }) =>
    request<{ id: string; existing: boolean }>("/inquiries", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  // ── 站內通知 ───────────────────────────────────────────────

  notifications: (limit = 20) => request<AppNotification[]>(`/notifications?limit=${limit}`),

  unreadNotifications: () => request<{ unread: number }>("/notifications/unread"),

  /** 不帶 id 就是全部已讀 */
  markNotificationsRead: (id?: string) =>
    request<{ ok: boolean }>("/notifications/read", {
      method: "POST",
      body: JSON.stringify(id ? { id } : {}),
    }),

  /**
   * 告訴對方我正在打字。
   *
   * 前端在有敲鍵盤時每兩秒送一次，後端寫的是五秒後的到期時間 ——
   * 所以不需要「停止打字」的事件，時間過了自然就沒了。
   */
  /**
   * 封存／取消封存。單邊生效 —— 從我的清單消失，對方不受影響。
   *
   * 刻意不是刪除：對話是雙方共有的，一邊按下刪除就讓另一邊的紀錄
   * 一起消失，是最容易被拿來抵賴的設計。
   */
  archiveConversation: (id: string) =>
    request<{ archived: boolean }>(`/conversations/${id}/archive`, { method: "POST" }),

  typing: (conversationId: string) =>
    request<{ until: string }>(`/conversations/${conversationId}/typing`, { method: "POST" }),


  // ── 合約與里程碑 ───────────────────────────────────────────

  contracts: () => request<Paginated<Contract>>("/contracts?pageSize=50"),

  contract: (id: string) => request<Contract>(`/contracts/${id}`),

  /** 里程碑進度。平台記錄雙方的聲明，不代表款項狀態。 */
  milestoneAction: (id: string, action: MilestoneAction) =>
    request<{ id: string; action: string; at: string }>(`/milestones/${id}`, {
      method: "POST",
      body: JSON.stringify({ action }),
    }),

  myProposals: () => request<Paginated<Proposal>>("/proposals?pageSize=50"),

  myJobs: () => request<Paginated<JobSummary>>("/my/jobs"),

  jobProposals: (jobId: string) => request<Paginated<Proposal>>(`/jobs/${jobId}/proposals`),

  /**
   * 結案並回報結果。
   *
   * 平台不抽成、錢也不經過這裡，所以看不見任何一筆成交 —— 而會員費是向
   * 接案者收的，續訂前一定會被問「我從這裡接到多少案子」。這支端點是
   * 唯一能回答那個問題的資料來源。
   */
  /** 我透過平台接到幾個案子。會員費的價值證明 */
  myHiredCount: () => request<{ hiredCount: number }>("/my/hired-count"),

  closeJob: (
    jobId: string,
    input: {
      outcome: "hired_on_platform" | "hired_elsewhere" | "no_hire";
      freelancerId?: string;
    },
  ) =>
    request<{ outcome: string; freelancerId: string | null; reportedAt: string }>(
      `/jobs/${jobId}/close`,
      { method: "POST", body: JSON.stringify(input) },
    ),

  // ── 信箱驗證 ───────────────────────────────────────────────

  sendVerification: () =>
    request<{ sent?: boolean; alreadyVerified?: boolean }>("/auth/send-verification", {
      method: "POST",
    }),

  /** 帳號設定頁用。/api/me 只回 session 的最小集合。 */
  myProfile: () => request<MyProfile>("/me/profile"),

  /**
   * 改變身分（接案／發案／兩者都要）。
   *
   * 註冊時那兩張卡片並排，選錯之後原本完全沒有辦法改 ——
   * 側欄會少掉一半的項目，而他無從得知那是身分的關係。
   */
  changeRole: (role: "client" | "freelancer" | "both") =>
    request<{ role: string }>("/me/role", { method: "PUT", body: JSON.stringify({ role }) }),

  /**
   * 上傳頭像。body 是縮圖後的 Blob，不是 FormData ——
   * 單一檔案不需要 multipart 的額外解析。
   */
  uploadAvatar: (blob: Blob) =>
    request<{ avatarUrl: string }>("/me/avatar", {
      method: "POST",
      headers: { "content-type": blob.type || "image/webp" },
      body: blob,
    }),

  deleteAvatar: () => request<{ avatarUrl: string }>("/me/avatar", { method: "DELETE" }),

  verifyEmail: (token: string) =>
    request<{ verified: boolean }>("/auth/verify-email", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),

  /**
   * 手機驗證：送出驗證碼。
   *
   * 回的是門號末四碼而不是完整號碼 —— 畫面上只需要讓使用者確認
   * 「是這支沒錯」，回完整號碼只是多一個外洩點。
   */
  /**
   * 登入的第二步。
   *
   * challenge 來自 login 失敗時丟出的 two_factor_required 的 details ——
   * 它只存在於剛剛那個瀏覽器的記憶體裡，不進網址也不進 localStorage。
   */
  loginTwoFactor: (challenge: string, code: string) =>
    request<{ userId: string }>("/auth/login/2fa", {
      method: "POST",
      body: JSON.stringify({ challenge, code }),
    }),

  setEmail2fa: (enabled: boolean) =>
    request<{ enabled: boolean }>("/me/email-2fa", {
      method: "POST",
      body: JSON.stringify({ enabled }),
    }),

  /** 要求換信箱。批准信會寄到**原**信箱，不是新的那個。 */
  requestEmailChange: (newEmail: string, password: string) =>
    request<{ sent: boolean; to: string; newEmail: string }>("/me/email/change", {
      method: "POST",
      body: JSON.stringify({ newEmail, password }),
    }),

  approveEmailChange: (token: string) =>
    request<{ approved: boolean; newEmail: string }>("/auth/email-change/approve", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),

  verifyEmailChange: (token: string) =>
    request<{ changed: boolean; email: string }>("/auth/email-change/verify", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),

  identityStatus: () =>
    request<{
      verified: boolean;
      /** 手機驗過了沒。沒過的話證件上傳一定會被拒絕，畫面要先擋 */
      phoneVerified: boolean;
      status: "pending" | "approved" | "rejected" | null;
      front: boolean;
      back: boolean;
      rejectReason: string | null;
      submittedAt: string | null;
    }>("/me/identity"),

  /**
   * 送出審核。
   *
   * 和上傳分開：上傳可以來回換照片，送出是一個他按下去、
   * 代表「我確認要送」的動作。原本沒有這一步 —— 傳完第二張
   * 畫面就自己換了，而他不知道是不是成功了。
   */
  submitIdentity: () =>
    request<{ status: "pending" }>("/me/identity/submit", { method: "POST" }),

  /** 上傳證件的一面。blob 已經在前端壓過並蓋上「僅供驗證使用」 */
  uploadIdentityDoc: (side: "front" | "back", blob: Blob, country?: string, docType?: string) =>
    request<{ front: boolean; back: boolean; complete: boolean }>(
      `/me/identity/${side}${qs({ country, docType })}`,
      { method: "POST", headers: { "content-type": blob.type || "image/webp" }, body: blob },
    ),

  phoneStart: (phone: string, countryCode?: string) =>
    request<{ sent: boolean; phoneHint: string }>("/me/phone/start", {
      method: "POST",
      body: JSON.stringify({ phone, ...(countryCode ? { countryCode } : {}) }),
    }),

  phoneCheck: (code: string) =>
    request<{ verified: boolean }>("/me/phone/check", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),

  withdrawProposal: (id: string) =>
    request<{ ok: boolean }>(`/proposals/${id}/withdraw`, { method: "POST" }),

  shortlistProposal: (id: string) =>
    request<{ status: string }>(`/proposals/${id}/shortlist`, { method: "POST" }),

  /**
   * 標記「我讀了這一份」。
   *
   * 由發案方展開提案內容的動作觸發，不是列表載入 —— 那樣
   * 「已讀」的意思會變成「頁面被打開過」，而二十份提案會一起
   * 被標成已讀，包括連捲動都沒捲到的。
   */
  markProposalViewed: (id: string) =>
    request<{ viewedAt: string }>(`/proposals/${id}/view`, { method: "POST" }),

  /**
   * 婉拒一份提案。理由選填。
   *
   * 不強制填理由 —— 強制會讓「婉拒」比「不理他」麻煩，
   * 而那正是我們想扭轉的行為。
   */
  declineProposal: (id: string, reason?: string) =>
    request<{ status: string }>(`/proposals/${id}/decline`, {
      method: "POST",
      body: JSON.stringify({ reason: reason?.trim() || undefined }),
    }),

  /** 選定人選、成立合約。整個平台最重要的一次寫入。 */
  acceptProposal: (id: string) =>
    request<{ contractId: string; milestones: number }>(`/proposals/${id}/accept`, {
      method: "POST",
    }),

  completeContract: (id: string) =>
    request<{ status: string }>(`/contracts/${id}/complete`, { method: "POST" }),

  submitReview: (contractId: string, rating: number, comment: string) =>
    request<{ rating: number }>(`/contracts/${contractId}/reviews`, {
      method: "POST",
      body: JSON.stringify({ rating, comment }),
    }),

  contractReviews: (contractId: string) =>
    request<{ mine: Review | null; received: Review | null }>(
      `/contracts/${contractId}/reviews`,
    ),
};

export interface AppNotification {
  /**
   * 事件名稱。畫面上的字由 notif.<kind>.title / .body 翻出來。
   *
   * title/body 只在字典查不到 kind 時當退路 —— 那是舊資料，
   * 它們在寫入的那一刻就被凍結成中文了。
   */
  params?: Record<string, string | number>;
  id: string;
  kind: string;
  title: string;
  body: string;
  /** 相對路徑，不含市場與語系前綴 —— 前端用 path() 補上 */
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface Conversation {
  id: string;
  market: string;
  /** 諮詢類的對話沒有案件 */
  jobId: string | null;
  /** job（從提案開始）| inquiry（主動諮詢） */
  kind: "job" | "inquiry";
  jobTitle: string;
  contractId: string | null;
  counterpartId: string;
  /** 對方的名字。後端補不到時會回「對方」，不會是空字串。 */
  counterpartName: string;
  /**
   * 對造的帳號已經不存在了（刪帳號、或資料重置留下的孤兒合約）。
   *
   * 和 counterpartName 為空是同一件事，但明說比較好 ——
   * 一個空字串在畫面上會被當成「還在載入」，而這是一個終局狀態。
   */
  counterpartMissing?: boolean;
  counterpartAvatar: string;
  /**
   * 對方註冊時填的國別，以及自己的。
   *
   * 兩個都回是刻意的：只給對方的話，使用者不會意識到自己的國籍
   * 也一直掛在對方眼前 —— 而嚇阻的前提正是「我知道對方看得到我」。
   * 單向的可見性只是監視，雙向的才是嚇阻。
   */
  counterpartCountry?: string | null;
  myCountry?: string | null;
  /**
   * 對方是不是正在打字。
   *
   * 由後端比對到期時間算好 —— 使用者的裝置時間可能差好幾分鐘，
   * 交給前端自己比會讓這個狀態永遠亮著或永遠不亮。
   */
  counterpartTyping: boolean;
  /** 這串對話裡「我」是哪一邊。後端算好，前端不用自己判斷。 */
  myRole: "client" | "freelancer";
  unread: number;
  archived: boolean;
  lastMessageAt: string;
  lastMessagePreview: string;
  lastFromMe: boolean;
  createdAt: string;
}

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  body: string;
  /** text = 人講的話；system = 里程碑等事件，兩者在同一條時間軸上 */
  /**
   * text   一般訊息
   * system 系統訊息
   * auto   離線代理人替他回的
   *
   * ── auto 一定要看得出來 ────────────────────────────────────
   *
   * 對方有權知道自己在跟一段程式說話。這一欄是唯一**不能被繞過**
   * 的誠實機制：提示詞可能被說服、文案可能被改，但畫面上的標記
   * 是從資料來的。
   */
  kind: "text" | "system" | "auto";
  meta?: Record<string, unknown> | null;
  /** 收回的訊息仍然存在，只是內容不顯示 */
  deleted: boolean;
  /**
   * 送出當下的來源國別（Cloudflare 邊緣節點寫的，偽造不了）。
   *
   * 只在它和對話所屬市場不同時才顯示 —— 一個台灣人在台灣發訊息
   * 不是資訊，而每則都掛國旗會讓那面國旗在第三則之後變成裝飾，
   * 真正該被注意的那一則也就跟著被忽略。
   *
   * IP 的雜湊**不會**回到前端：它是給後台比對用的，
   * 而雜湊擋得住反查、擋不住比對。
   */
  senderCountry?: string | null;
  createdAt: string;
}

export type MilestoneAction = "start" | "submit" | "approve" | "mark_paid";

export interface Milestone {
  id: string;
  contractId: string;
  title: string;
  /** 雙方約定的金額。平台不經手這筆錢，這只是紀錄。 */
  amountMinor: number;
  status: "pending" | "in_progress" | "submitted" | "approved";
  dueAt: string;
  sortOrder: number;
  submittedAt: string | null;
  approvedAt: string | null;
  /** 發案方的單方註記，不是平台的證明 */
  paidMarkedAt: string | null;
}

/**
 * 一件正在進行的工作。
 *
 * 平台成交的與接案者自己帶進來的是**同一種東西** —— 差別只在
 * source 與有沒有 jobId。分成兩個概念的話，里程碑、進度、文書、
 * 請款全部要寫兩遍，而且兩份實作一定會長歪。
 */
export interface Contract {
  id: string;
  market: string;
  /** 站外專案沒有這三個 */
  jobId: string | null;
  proposalId: string | null;
  clientId: string | null;
  freelancerId: string;
  /** platform（平台成交）| external（自己帶進來的） */
  source: "platform" | "external";
  /** 站外專案自己取的名字；平台成交的沿用案件標題 */
  title: string;
  externalClientName: string | null;
  externalClientEmail: string | null;
  externalClientNote: string | null;
  pricing: string;
  totalValueMinor: number;
  currency: string;
  feeBps: number;
  status: "active" | "completed" | "cancelled" | "disputed";
  startedAt: string;
  endedAt: string | null;
  /** 給客戶看的公開進度頁 */
  progressToken: string | null;
  progressShared: boolean;
  progressNote: string;
  progressUpdatedAt: string | null;
  /**
   * 對方是誰。後端依登入者算好 —— 卡片上少了這個，
   * 一個人手上有幾個案子時就完全分不出哪張是哪張。
   */
  counterpartName: string;
  /**
   * 對造的帳號已經不存在了（刪帳號、或資料重置留下的孤兒合約）。
   *
   * 和 counterpartName 為空是同一件事，但明說比較好 ——
   * 一個空字串在畫面上會被當成「還在載入」，而這是一個終局狀態。
   */
  counterpartMissing?: boolean;
  counterpartAvatar: string;
  milestones: Milestone[];
}

export interface Review {
  id: string;
  contractId: string;
  authorId: string;
  targetId: string;
  rating: number;
  comment: string;
  createdAt: string;
}

export interface Proposal {
  id: string;
  market: string;
  jobId: string;
  freelancerId: string;
  clientId: string;
  coverLetter: string;
  bidAmountMinor: number;
  estimatedDays: number;
  status: "submitted" | "shortlisted" | "accepted" | "declined" | "withdrawn";
  /**
   * 發案方展開這份提案的時間。沒有值就是還沒有人打開過。
   *
   * status 是「決定」，這一格是「有沒有被看」。兩者分開，因為
   * 「已讀但還在考慮」和「根本沒人看」對接案者是完全不同的訊息，
   * 而它們在這之前長得一模一樣。
   */
  viewedAt?: string | null;
  submittedAt: string;
  updatedAt: string;
}

export interface ContractDocument {
  id: string;
  version: number;
  body: string;
  /** 內容雜湊。雙方的副本上都印著它 —— 對不上就代表文件被動過 */
  contentHash: string;
  clientSignedAt: string | null;
  clientSignerName: string | null;
  freelancerSignedAt: string | null;
  freelancerSignerName: string | null;
  /** 兩邊都簽完了。有值就代表這一版鎖住，要改只能出新版 */
  fullySignedAt: string | null;
  createdAt: string;
}

export interface CheckoutSession {
  provider: "paypal" | "ecpay";
  orderNo: string;
  providerRef: string;
  /**
   * 兩家金流商送使用者去付款的方式不同：
   *  - redirect ：直接導向 url（PayPal）
   *  - form_post：把 fields 組成表單 POST 到 url（綠界 AIO）
   */
  mode: "redirect" | "form_post";
  url: string;
  fields?: Record<string, string>;
  expiresAt: string;
}

export interface MembershipOrder {
  orderNo: string;
  provider: string;
  cycle: string | null;
  amountMinor: number;
  chargeCurrency: string;
  chargeAmountMinor: number;
  status: "pending" | "authorized" | "captured" | "failed" | "refunded" | "expired";
  createdAt: string;
  capturedAt: string | null;
}

// ══════════════════════════════════════════════════════════════════
// React hook
// ══════════════════════════════════════════════════════════════════

export interface AsyncState<T> {
  data: T | null;
  error: ApiRequestError | null;
  loading: boolean;
  reload: () => void;
}

/**
 * 簡易資料取得 hook。
 * 刻意不引入 React Query —— 這個站的資料需求單純，
 * 多一個函式庫要多背 15KB，等真的需要快取共享再換。
 *
 * deps 變動時會自動重新取；元件卸載後不會 setState（避免 race）。
 */
export function useApi<T>(fetcher: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiRequestError | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  // fetcher 每次 render 都是新函式，用 ref 存住避免無限迴圈
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetcherRef
      .current()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof ApiRequestError
              ? err
              : new ApiRequestError("internal_error", String(err), 0),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, error, loading, reload };
}

// ══════════════════════════════════════════════════════════════════
// 回應型別（對應 gateway 實際回傳的形狀）
// ══════════════════════════════════════════════════════════════════

export interface MyProfile {
  id: string;
  handle: string;
  email: string;
  displayName: string;
  avatarUrl: string;
  role: string;
  market: string;
  timezone: string;
  verifications: string[];
  emailVerifiedAt: string | null;
  /** 二步驗證開啟的時間。null 表示沒開。 */
  email2faEnabledAt: string | null;
  /** E.164 門號。沒驗證過就是 null */
  phone: string | null;
  phoneVerifiedAt: string | null;
  /** 證件驗證通過的時間。null 表示還沒過 */
  idVerifiedAt: string | null;
  memberSince: string;
  suspendedAt: string | null;
  membership: {
    plan: string;
    startedAt: string;
    paidUntil: string | null;
    cycle: string | null;
    freeProposalsUsed: number;
    /** 首購的成交額度還剩幾次。null＝不在這個階段。 */
    firstPurchaseWinsLeft: number | null;
    emailVerified: boolean;
  } | null;
}

export interface Actor {
  userId: string;
  role: string;
  market: string;
  handle: string;
  displayName: string;
  avatarUrl: string;
  /** 投標與主動諮詢的前置條件。跟著 session 一起帶，不用另外查。 */
  emailVerified: boolean;
  /**
   * 手機驗證過了沒。
   *
   * gateway 一直有回這一欄，但這個型別沒有宣告它 —— 於是前端拿不到，
   * decideBidding 收到 undefined 就放行，而伺服器那邊拿到的是
   * Boolean(phoneVerifiedAt)（沒驗過就是 false）並擋下。
   * 「畫面說可以、送出說不行」就是這樣來的。
   *
   * 可選是刻意的：舊的 gateway 不回這一欄時視為通過，
   * 跨服務部署的那幾分鐘不該把所有人鎖在外面。
   */
  phoneVerified?: boolean;
  /** 證件驗證過了沒。投標與主動聯絡的第三道 */
  idVerified?: boolean;
}

export interface MarketInfo {
  code: string;
  slug: string;
  name: string;
  defaultLocale: string;
  locales: string[];
  currency: string;
  timezone: string;
  paymentProvider: string;
}

export interface BootstrapData {
  actor: Actor | null;
  /**
   * 目前使用者的會員狀態。發案方與未登入者是 null。
   *
   * 放在 bootstrap 而不是各頁自己查：投標的入口有好幾個，
   * 每個地方各查一次遲早會漏掉一處，而漏掉的那一處就是
   * 「看得到按鈕、按了才說不行」。
   */
  membership: {
    plan: string;
    startedAt: string;
    paidUntil: string | null;
    cycle: string | null;
    /** 已用掉幾次免費提案 */
    freeProposalsUsed: number;
    /**
     * 證件驗證前已用掉幾次額度。
     *
     * 這一欄必須帶到前端，理由和 phoneVerified 那一欄一樣：
     * decideBidding 收到 undefined 會當成 0 而放行，於是畫面畫出
     * 投標表單、伺服器卻回 id_unverified —— 一個讓人白寫十分鐘
     * 提案的 bug。
     */
    preIdUsed: number;
    emailVerified: boolean;
  } | null;
  market: MarketInfo;
  markets: { code: string; slug: string; name: string }[];
}

export interface Category {
  slug: string;
  name: string;
  icon: string;
  accent: string;
  jobCount: number;
  /**
   * 這個分類裡有多少位接案者。
   *
   * 由 gateway 合併 identity 的資料而來 —— jobs 服務不知道這個數字，
   * 而它也不該為了湊這個數字去呼叫 identity。
   */
  talentCount: number;
}

export interface Skill {
  slug: string;
  name: string;
  categorySlug: string;
  popularity: number;
}

export interface JobSummary {
  id: string;
  slug: string;
  market: string;
  title: string;
  /**
   * 只有發案方看自己的案件時才有。
   *
   * 公開列表不回這個欄位 —— 內文在付費牆後面，而列表本來就
   * 只該給名稱與價格。型別上是選填的，好讓忘記判斷的地方
   * 在編譯時就被擋下來，而不是在線上安靜地印出一段不該印的字。
   */
  description?: string;
  clientId: string;
  categorySlug: string;
  pricing: "fixed" | "hourly";
  budgetMinMinor: number;
  budgetMaxMinor: number;
  duration: string;
  projectSize: string;
  experienceLevel: string;
  status: string;
  proposalCount: number;
  viewCount: number;
  featured: boolean;
  /**
   * 上線時放的示範案件，另一頭沒有人。
   *
   * 列表留著它們（空列表看起來像壞掉的站），但它必須在畫面上
   * 說出自己是示範 —— 而且後端擋掉投標，見 shared/jobs.ts。
   * 只擋後端的話，使用者是寫完提案按下送出才知道。
   */
  isDemo?: boolean;
  postedAt: string;
  skillSlugs: string[];
}

export interface JobDetail extends JobSummary {
  /**
   * 內文被鎖住了嗎（信箱＋手機都驗證過才看得到）。
   *
   * gateway 在鎖住時會把 description 換成空字串並附上這個旗標 ——
   * 少了旗標的話，畫面只會顯示一個空白的說明區塊，
   * 使用者會以為是發案方沒寫，或是系統壞了。
   */
  contentLocked?: boolean;
  /**
   * 為什麼被鎖住 —— 兩種原因兩種解法。
   *
   *   verification  去驗信箱／手機／證件
   *   membership    去繳會員費
   *
   * 少了這一欄的話，畫面只能同時列出所有按鈕，而一個已經
   * 驗證完、只差繳費的人會看到三個和他無關的驗證按鈕，
   * 然後以為自己驗證沒過。
   */
  lockReason?: "guest" | "verification" | "membership";
  similar: Pick<
    JobSummary,
    "id" | "slug" | "title" | "budgetMinMinor" | "budgetMaxMinor" | "pricing" | "proposalCount" | "postedAt"
  >[];
  client: {
    /** 主動聯絡發案方時要用 */
    id: string;
    displayName: string;
    countryCode: string;
    memberSince: string;
    verifications: string[];
    profile: {
      companyName: string | null;
      totalSpentMinor: number;
      jobsPosted: number;
      hireRate: number;
      /**
       * 回應過的提案數與逾期未回的提案數。
       *
       * 這兩個數字一直有在記錄，但從來沒有顯示在任何地方 ——
       * 而它們回答的是接案者最想知道的那個問題：
       * 「我投了會不會石沉大海」。
       */
      proposalsResponded: number;
      proposalsExpired: number;
      rating: number;
      reviewCount: number;
    };
  } | null;
}

export interface TalentSummary {
  id: string;
  handle: string;
  displayName: string;
  avatarUrl: string;
  market: string;
  countryCode: string;
  city: string | null;
  timezone: string;
  languages: { code: string; level: string }[];
  verifications: string[];
  avgResponseHours: number;
  memberSince: string;
  headline: string;
  bio: string;
  experienceLevel: string;
  categorySlug: string;
  hourlyRateMinor: number;
  rating: number;
  reviewCount: number;
  successRate: number;
  jobsCompleted: number;
  available: boolean;
  weeklyHours: number;
  membershipFreeUntil: string | null;
    membershipPaidUntil: string | null;
  skillSlugs: string[];
}

export interface TalentDetail extends Omit<TalentSummary, "headline" | "bio"> {
  profile: {
    headline: string;
    bio: string;
    experienceLevel: string;
    categorySlug: string;
    hourlyRateMinor: number;
    rating: number;
    reviewCount: number;
    successRate: number;
    jobsCompleted: number;
    available: boolean;
    weeklyHours: number;
    membershipFreeUntil: string | null;
    membershipPaidUntil: string | null;
  };
  /**
   * 已驗證的外部作品集帳號。
   *
   * 這是發案方唯一會看的地方 —— 一個只出現在「編輯個人檔案」
   * 頁面的驗證徽章，對決定要不要僱用他的那個人是完全不存在的。
   */
  portfolioProofs?: {
    provider: string;
    handle: string;
    profileUrl: string;
    verifiedAt: string;
    stats: Record<string, unknown>;
  }[];
  portfolio: {
    id: string;
    title: string;
    description: string;
    imageUrl: string;
    url: string | null;
    tags: string[];
  }[];
}

export interface ActivityItem {
  id: string;
  market: string;
  kind: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface ProposalSlots {
  cap: number;
  used: number;
  slotsLeft: number;
}

export interface CreateProposalResult {
  id: string;
  slotsLeft: number;
  /** 送出後還剩幾次免費提案。付費會員回 null（不限次數）。 */
  freeProposalsLeft: number | null;
  fee: {
    grossMinor: number;
    feeBps: number;
    feeMinor: number;
    netMinor: number;
    waived: boolean;
  };
}

export interface MarketStats {
  openJobs: number;
  totalBudgetMinor: number;
  postedThisWeek: number;
}

export interface FreelancerProfileForm {
  headline: string;
  bio: string;
  experienceLevel: "entry" | "intermediate" | "expert";
  categorySlug: string;
  /** 主要單位（元），不是分 —— 表單填什麼就送什麼 */
  hourlyRate: number;
  available: boolean;
  weeklyHours: number;
  skills: string[];
}

export interface Expense {
  id: string;
  spentAt: string;
  currency: string;
  amountMinor: number;
  vendor: string;
  category: string;
  note: string;
  hasImage: boolean;
  /** AI 讀出來的。畫面要標「請確認」—— 沒標記的錯誤金額會被拿去報稅 */
  fromOcr: boolean;
}

export interface ExpenseSummary {
  category: string;
  currency: string;
  totalMinor: number;
  count: number;
}

export interface ExpenseInput {
  spentAt: string;
  /**
   * 選填 —— 不送的話由伺服器從路由上的市場決定。
   *
   * 標成必填的時候，每一個呼叫點都得自己想一個值，
   * 而它們全部想成了 "TWD"（見 services/contracts 的 moneyCurrency）。
   * 一個所有人都得回答、但沒有人有足夠資訊回答的問題，
   * 答案就會是複製貼上來的。
   */
  currency?: string;
  amount: number;
  vendor: string;
  category: string;
  note: string;
}

export interface AgentSettings {
  enabled: boolean;
  /** 離線到什麼時候。空的代表沒設定 —— 而沒設定就不會回覆 */
  awayUntil: string | null;
  greeting: string;
  /** budget / timeline / scope / deliverables / references */
  checklist: string[];
  /**
   * 每段對話最多回幾次。
   *
   * 上限存在的理由不是成本，是兩個代理人會互相回覆 ——
   * 對方如果也開著自動回覆，兩邊會一直客氣地問下去。
   */
  maxReplies: number;
}

export interface AgentCollected {
  conversationId: string;
  count: number;
  /** 已經**答到**的項目（鍵）。注意不是「問過的」 */
  collected: string[];
  /**
   * 答案本身：{"budget":"約 NT$3,000","timeline":"年底前"}
   *
   * 接案者回來要讀的是這個。「已問到：預算、時程」對報價沒有幫助 ——
   * 他要知道的是那個預算是多少。
   */
  facts: Record<string, string>;
  /** 給接案者的警訊，例如「預算與描述的範圍差距很大」 */
  flags: string[];
  lastAt: string;
  jobTitle: string | null;
}

export interface AiQuota {
  allowed: boolean;
  limit: number;
  used: number;
  left: number;
  /** ok | no_membership | exhausted —— 後兩者要說不同的話 */
  reason: string;
  /**
   * 會員狀態：active | trial | expired | exhausted | none
   *
   * reason 回答的是「這一次呼叫准不准」，state 回答的是「他是什麼身分」。
   * 只有付費會員能用的功能要看 state —— 免費期的人 reason 是 "ok"，
   * 但那不代表每一個 AI 功能都對他開放。
   */
  state?: string;
}

export interface CompletedProject {
  id: string;
  title: string;
  currency: string;
  totalValueMinor: number;
  weeks: number;
  endedAt: string | null;
  /** 只有站外案子才給 —— 平台上的發案方沒有同意讓名字出現在別人的作品集裡 */
  clientName: string;
  rating: number | null;
  comment: string;
}

export interface PortfolioInput {
  title: string;
  description: string;
  imageUrl: string;
  url: string;
  tags: string[];
}

export interface PortfolioItem extends PortfolioInput {
  id: string;
  sortOrder: number;
}

// ── 流動辦公室的型別 ──────────────────────────────────────────

/**
 * 請款單與報價單的狀態刻意不重疊。
 *
 * 財務頁算「待收款」抓的是 "sent" —— 報價單如果也用 "sent"，
 * 一張還在等回覆的報價會被算成應收帳款，而那是一筆不存在的錢。
 */
export type InvoiceStatus = "draft" | "sent" | "paid" | "void";
export type QuoteStatus = "quote_draft" | "quote_sent" | "quote_accepted" | "quote_declined";
export type DocStatus = InvoiceStatus | QuoteStatus;

export interface ProjectInput {
  title: string;
  clientName: string;
  clientEmail: string;
  clientNote: string;
  pricing: "fixed" | "hourly";
  /** 主要單位（元），換算成分在後端做 */
  totalValue: number;
  /**
   * 選填 —— 不送的話由伺服器從路由上的市場決定。
   *
   * 標成必填的時候，每一個呼叫點都得自己想一個值，
   * 而它們全部想成了 "TWD"（見 services/contracts 的 moneyCurrency）。
   * 一個所有人都得回答、但沒有人有足夠資訊回答的問題，
   * 答案就會是複製貼上來的。
   */
  currency?: string;
  milestones: { title: string; amount: number }[];
}

export interface InvoiceLine {
  description: string;
  /** 數量放大 100 倍存整數，1.5 小時 = 150 */
  quantityCenti: number;
  unitPriceMinor: number;
  amountMinor: number;
}

export interface InvoiceInput {
  contractId: string;
  clientName: string;
  clientEmail: string;
  clientDetails: string;
  /**
   * 選填 —— 不送的話由伺服器從路由上的市場決定。
   *
   * 標成必填的時候，每一個呼叫點都得自己想一個值，
   * 而它們全部想成了 "TWD"（見 services/contracts 的 moneyCurrency）。
   * 一個所有人都得回答、但沒有人有足夠資訊回答的問題，
   * 答案就會是複製貼上來的。
   */
  currency?: string;
  /** 稅率基點。台灣營業稅 5% = 500 */
  taxBps: number;
  dueAt: string;
  note: string;
  items: { description: string; quantity: number; unitPrice: number }[];
}

export interface Invoice {
  id: string;
  number: string;
  contractId: string | null;
  clientName: string;
  clientEmail: string | null;
  clientDetails: string;
  currency: string;
  taxBps: number;
  subtotalMinor: number;
  taxMinor: number;
  totalMinor: number;
  status: DocStatus;
  /** invoice | quote */
  kind: string;
  /** 這張請款單是由哪張報價單轉來的 */
  fromQuoteId: string | null;
  shareToken: string;
  /** 公開連結有沒有開放。關掉之後 /public/invoices/:token 回 404 */
  shared: boolean;
  issuedAt: string;
  dueAt: string | null;
  paidAt: string | null;
  note: string;
}

/** 公開版本刻意少了 shareToken、freelancerId 與內部 id */
export interface PublicInvoice {
  number: string;
  clientName: string;
  clientDetails: string;
  currency: string;
  taxBps: number;
  subtotalMinor: number;
  taxMinor: number;
  totalMinor: number;
  status: InvoiceStatus;
  issuedAt: string;
  dueAt: string | null;
  note: string;
  items: InvoiceLine[];
}

/*
  產能的型別直接沿用共用模組的，不再重宣告一份。

  重宣告的代價已經在這裡發生過：level 在那邊是
  "none" | "warn" | "full" | "over"，在這裡被寫成 string，
  於是把 API 的回傳丟給共用的函式時型別對不上 —— 而那還算幸運，
  因為編譯器抓到了。分岔在別的欄位上可能就靜靜地過去了。

  hourlyRateMinor 是端點額外附上的（前端算「再接一個會怎樣」
  要用），所以用 extends 加一欄，而不是複製整份。
*/
export interface Benchmark {
  /**
   * 樣本夠不夠。false 時所有數字都是 null。
   *
   * 五個人的「中位數」在一個彼此認識的小圈子裡等於公開收入 ——
   * 而這件事出錯一次就再也回不去了。
   */
  enough: boolean;
  peers: number;
  needed: number;
  medianMinor: number | null;
  p25Minor: number | null;
  p75Minor: number | null;
  /** 我今年的已收款。沒有就是 null */
  myTotalMinor: number | null;
  /** 我在第幾百分位（0–1）。沒有收入就是 null */
  percentile: number | null;
}

export interface SeasonMonth {
  /** 1–12 */
  month: number;
  totalMinor: number;
}

export interface GrowthYear {
  year: number;
  currency: string;
  totalMinor: number;
  invoices: number;
  clients: number;
  /** 這一年有付錢、而且更早以前也付過錢的客戶 */
  repeatClients: number;
  /** 沒有客戶的年份是 null —— 0 讀起來像「一個都沒回頭」 */
  repeatRate: number | null;
  loggedMinutes: number;
  /** 那一年的實際時薪。沒記工時是 null —— 0 讀起來像那一年白做了 */
  effectiveRateMinor: number | null;
}

export interface TimeEntry {
  id: string;
  contractId: string | null;
  startedAt: string;
  /** null 表示正在計時 */
  endedAt: string | null;
  minutes: number;
  note: string;
  billable: boolean;
}

export interface TimeEntryInput {
  contractId?: string;
  startedAt: string;
  minutes: number;
  note: string;
  billable: boolean;
}

export interface TimedProject {
  id: string;
  title: string;
  currency: string;
  totalValueMinor: number;
  status: string;
  minutes: number;
  billableMinutes: number;
  /**
   * 實際時薪。分母是**全部**工時，不是只算可請款的 ——
   * 只算可請款的會得到一個比較好看的數字，而他想知道的是
   * 這段時間的實際回報。
   */
  effectiveRateMinor: number | null;
}

export interface TimeLog {
  entries: TimeEntry[];
  projects: TimedProject[];
}

export interface JobMatch {
  id: string;
  slug: string;
  title: string;
  categorySlug: string;
  budgetMinMinor: number;
  budgetMaxMinor: number;
  pricing: string;
  postedAt: string;
  /** 技能重疊幾項 */
  overlap: number;
  /** 重疊到哪幾項 —— 說得出來，他才會相信這個推薦 */
  matched: string[];
  /** 他總共有幾項技能。overlap / of 才是「有多合」 */
  of: number;
}

export type { ProjectLoad } from "@shared/capacity";

export interface CapacityReport extends CapacityBase {
  /** 端點額外附上 —— 前端算「再接一個會怎樣」要用 */
  hourlyRateMinor: number;
}

export interface PricingGuidance {
  /** 有幾筆可用的樣本 */
  sample: number;
  /**
   * 夠不夠。false 時下面的數字一律是 null。
   *
   * 三筆資料也算得出一個中位數，而那個數字看起來和三百筆算出來的
   * 一模一樣 —— 使用者沒有線索分辨，他會照著它報價。
   */
  enough: boolean;
  ratioP25: number | null;
  ratioMedian: number | null;
  ratioP75: number | null;
  suggestLowMinor: number | null;
  suggestMidMinor: number | null;
  suggestHighMinor: number | null;
  /** 有沒有成交資料可以談得標率。目前一律 false —— 平台還沒有成交紀錄 */
  hasOutcomeData: boolean;
}

// ── 客戶名冊的型別 ──────────────────────────────────────────

export interface ClientInput {
  name: string;
  email: string;
  phone: string;
  company: string;
  /** 統編／VAT／EIN —— 純文字，各國叫法不同 */
  taxId: string;
  note: string;
}

export interface ClientInviteStatus {
  /** pending | accepted | expired | revoked */
  status: string;
  email: string;
  expiresAt: string;
  createdAt: string;
}

export interface CrmClient extends ClientInput {
  id: string;
  /** platform（也是平台使用者）| external（只在他自己的名冊裡） */
  source: string;
  archived: boolean;
  createdAt: string;

  // ── 彙總。這是自由文字做不到的事 ──
  invoiceCount: number;
  projectCount: number;
  paidMinor: number;
  outstandingMinor: number;
  /** 主要幣別（金額最大的那個）。一筆單據都沒有時是 null */
  currency: string | null;
  /** 還有幾種其他幣別。加總沒有意義，所以只說有 */
  otherCurrencies: number;
  /**
   * 平均拖款天數。只算已經付掉的 ——
   * 把未付的算進去會得到一個一直在變大的數字，
   * 而那個數字描述的是「還沒付」，不是「他付款有多慢」。
   */
  avgDaysToPay: number | null;
  lastActivity: string | null;
  /** 這個客戶總共花掉他幾分鐘 */
  loggedMinutes: number;
  /**
   * 每小時實際時薪。
   *
   * 分子是合約金額，不是已收款 —— 一個還沒付錢的客戶不代表這段
   * 時間沒有價值，而「他還沒付」是拖款天數那一欄的事。
   *
   * 沒記工時就是 null，不是 0：「時薪 0 元」讀起來像
   * 「這個客戶不值得」，而實際上是「還沒記工時」。
   */
  effectiveRateMinor: number | null;
}

// ── 財務的型別 ──────────────────────────────────────────────

/**
 * 一筆收款。
 *
 * 每一筆都要給，不能只給總額 —— 台灣的二代健保補充保費是按
 * **單次給付**扣繳的，年收 24 萬拆成 24 次各一萬和拆成 12 次
 * 各兩萬，結果完全不同。
 */
export interface FinanceEntry {
  id: string;
  number?: string;
  clientName: string;
  currency: string;
  amountMinor: number;
  at: string | null;
  source: "invoice";
}

export interface FinanceOutstanding {
  id: string;
  number: string;
  clientName: string;
  currency: string;
  amountMinor: number;
  issuedAt: string;
  dueAt: string | null;
}

export interface FinancePipeline {
  id: string;
  title: string;
  clientName: string;
  currency: string;
  amountMinor: number;
  startedAt: string;
  source: string;
}

export interface ExternalIncomeRow {
  id: string;
  receivedAt: string;
  currency: string;
  amountMinor: number;
  note: string;
  /** freelance | salary | other —— 決定稅務上套哪一條規則 */
  kind: string;
}

export interface ExternalIncomeInput {
  receivedAt: string;
  currency: string;
  amount: number;
  note: string;
  kind: "freelance" | "salary" | "other";
}

export interface TaxSettingsInput {
  /** 每月固定支出。0 表示還沒填 —— 和「每月需要 0 元」是兩件事 */
  monthlyNeed: number;
  /** 成本費用率，基點（3000 = 30%）。用基點避免浮點誤差累積 */
  expenseRateBps: number;
  salaryIncome: number;
  otherIncome: number;
  people: number;
  filingStatus: "single" | "married";
  itemisedDeduction: number;
}

export interface FinanceData {
  year: number;
  paid: FinanceEntry[];
  outstanding: FinanceOutstanding[];
  pipeline: FinancePipeline[];
  external: ExternalIncomeRow[];
  /**
   * 沒設定過就是 null —— 預設值由前端從該市場的稅制取。
   *
   * 後端不寫死一組預設值：成本費用率的合理起點各國不同
   * （台灣有書審純益率、美國是核實認列所以是 0），而且會隨年度變。
   *
   * 金額是最小單位，和資料庫一致 —— 表單顯示時才除以 100。
   * 型別上和 TaxSettingsInput 刻意不同：那個是**送出去**的形狀，
   * 用「元」，因為使用者填的是元。兩個混用會出現一個
   * 除以 100 除了兩次的 bug，而它在畫面上只是「數字變小了」。
   */
  settings: {
    expenseRateBps: number;
    salaryIncomeMinor: number;
    otherIncomeMinor: number;
    people: number;
    filingStatus: "single" | "married";
    itemisedDeductionMinor: number;
    monthlyNeedMinor: number;
  } | null;
}

export interface OfficeSummary {
  year: number;
  /** 幣別分開列 —— 不做匯率換算，亂換出來的總額比沒有更糟 */
  byStatus: { status: InvoiceStatus; currency: string; totalMinor: number; count: number }[];
  activeProjects: number;
}

// ── 行動辦公室的型別 ──────────────────────────────────────────

export type NoteKind = "note" | "meeting" | "spec" | "decision";

export interface NoteInput {
  kind: NoteKind;
  title: string;
  body: string;
  /** 要不要出現在給客戶的進度頁。預設不分享。 */
  shared: boolean;
  occurredAt: string;
}

export interface ProjectNote extends NoteInput {
  id: string;
  contractId: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectTask {
  id: string;
  contractId: string | null;
  title: string;
  dueAt: string | null;
  doneAt: string | null;
  sortOrder: number;
  createdAt: string;
}

export interface PublicProgress {
  title: string;
  status: string;
  startedAt: string;
  note: string;
  updatedAt: string | null;
  milestones: { title: string; status: string; dueAt: string; approvedAt: string | null }[];
  notes: { kind: NoteKind; title: string; body: string; occurredAt: string | null; createdAt: string }[];
  /** 只有統計，不回內容 —— 待辦是接案者自己的工作切分 */
  tasks: { total: number; done: number };
}

export interface Analytics {
  /** 統計是從哪一天開始有的。null＝還沒有任何資料。 */
  trackingSince: string | null;
  daily: { day: string; impressions: number; views: number }[];
  profile: {
    rating: number;
    reviewCount: number;
    jobsCompleted: number;
    successRate: number;
  } | null;
  /**
   * 檔案缺了什麼。
   *
   * 「點擊率低」對使用者是一句沒有用的話 —— 他要知道**為什麼**，
   * 而且要是他能動手改的原因。
   */
  completeness: {
    hasAvatar: boolean;
    hasHeadline: boolean;
    hasBio: boolean;
    portfolioCount: number;
    skillCount: number;
    available: boolean;
    reviewCount: number;
    jobsCompleted: number;
  } | null;
}
